import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

const ConflictDialog = ({ state, onCancel, onConfirm }) => (
  <AlertDialog open={!!state} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent data-testid="conflict-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-5 h-5" /> {state?.message || "Scheduling conflict"}
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(state?.conflicts || []).map((c, i) => (
              <p key={i} className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`conflict-item-${i}`}>
                {c.message}
              </p>
            ))}
            <p className="text-xs text-slate-500">Includes trip time, waiting, return trips, travel between bookings and a 15-minute buffer.</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="conflict-cancel-btn">Go Back</AlertDialogCancel>
        <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} className="bg-amber-600 hover:bg-amber-700" data-testid="conflict-override-btn">
          Assign Anyway
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Runs an API call; on a 409 conflict shows a warning and retries with force=true if dispatch confirms.
export function useConflictGuard() {
  const [state, setState] = useState(null);

  const guard = async (run) => {
    try {
      return await run(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) {
        return new Promise((resolve, reject) => setState({ ...d, run, resolve, reject }));
      }
      throw e;
    }
  };

  const onCancel = () => {
    state?.resolve(null);
    setState(null);
  };
  const onConfirm = async () => {
    const s = state;
    setState(null);
    try {
      s.resolve(await s.run(true));
    } catch (err) {
      s.reject(err);
    }
  };

  return { guard, conflictDialog: <ConflictDialog state={state} onCancel={onCancel} onConfirm={onConfirm} /> };
}

export default useConflictGuard;
